import React, { useState, useEffect, useCallback, useRef } from 'react';
import EnhancedTable from '../../components/table';
import Config from '../../config';
import Moment from 'react-moment';
import { Toolbar, Button, ButtonGroup, Dialog, DialogContent, DialogActions, TextField, Switch, FormControlLabel, Chip, Typography, Checkbox, Grid, TableCell, Alert } from '@mui/material';
import { Link } from 'react-router-dom';
import { SimpleRte } from '../../components/EnhancedRte';
import ExtendedSelectComponent from '../../components/select';
import EnhancedSelectComponent from '../../components/EnhancedSelect';
import DocumentLink from '../../components/document_link';
import axios from 'axios';
import Auth from '../../auth';
import DownloadButton from '../../components/download_button';
import { getCompanyNames, parseSignalrMessage } from '../../helpers';
import { PostStatusChip, postStatuses, writeStatuses, ChangeStatusModal } from './thread';
import RepliedIcon from '@mui/icons-material/Lens';
import isEqual from 'lodash.isequal';
import { DeleteDialog, SaveDialog, SaveAsDialog } from '../files/import_export_profiles';
import { Root, Header, defaultResultsContainerStyles } from '../../theme';
import CatalogManagerResults from '../../components/CatalogManagerResults';
import { TABLE_TYPES } from '../../components/saved_search/utils/savedSearchConstants';
import { useDidUpdateEffect } from '../../utils/utils';

// ExtendedSelect is a JS component; its inferred type only exposes RefAttributes,
// so cast to ComponentType<any> to allow the variant/options/onChange props.
const ExtendedSelect = ExtendedSelectComponent as React.ComponentType<any>;
// EnhancedSelect is a forwardRef whose strict props reject the loose caller shape.
const EnhancedSelect = EnhancedSelectComponent as unknown as React.ComponentType<any>;
// EnhancedTable is `withRouter(class)` — withRouter narrows the wrapped props to
// Pick<RouteComponentProps, never>, hiding config/template/history. CatalogManagerResults
// is a .js default export with no explicit Props. Cast both permissively so the
// .tsx builds under fork-ts-checker (same pattern as attachments_v2.tsx).
const TypedEnhancedTable = EnhancedTable as React.ComponentType<any>;
const TypedCatalogManagerResults = CatalogManagerResults as React.ComponentType<any>;
// styled(Typography) strips the OverridableComponent typing so `component` isn't visible.
const PageHeader = Header as React.ComponentType<any>;

export const statusOptions = postStatuses.map(r => ({value: r, label: r}));
// writeStatuses currently mirrors postStatuses (no display-only status); kept as a
// separate export so write dropdowns stay decoupled from the filter/display set.
export const writeStatusOptions = writeStatuses.map(r => ({value: r, label: r}));

interface MessageProfile {
    Id?: number;
    Name: string;
    Subject: string;
    Body: string;
    Shared?: boolean;
}

interface SelectOption {
    label: string;
    value: number | string;
}

interface SelectedThread {
    Id: number | string;
    [key: string]: unknown;
}

interface TableUrlBuilder {
    filter?: string;
    orderBy?: string;
}

const signalrUpdate = async function(rawMessage: unknown, page: any[], signalrOpts?: unknown){
    const message: any = parseSignalrMessage(rawMessage);
    if(!message.additionalData){
        return null;
    }
    let existing = page.find((r: { Id: number | string }) => r.Id === message.additionalData.ThreadId);
    if(message.action === 'newThreadMessage' && existing && message.additionalData.Category === "Status"){
        return {Id: existing.Id, Status: message.additionalData.Status, LastUpdated: message.additionalData.Date};
    }
    return null;
}

// Quick filter: narrows the list by message Status. Applies a `Status eq <value>`
// filter alongside any partner filter (AND logic) and syncs back from table state
// so saved searches / URL restores reflect the active selection.
function MessageStatusFilter(props: { table: any }) {
    const { table } = props;
    const [selected, setSelected] = useState<SelectOption | null>(null);

    useEffect(() => {
        const filters = table?.state?.filter?.filters || [];
        const existing = filters.find((f: any) => f.field === 'Status' && f.operator === 'eq');
        setSelected(existing ? { label: existing.value, value: existing.value } : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [table?.state?.filter]);

    const onChange = (value: SelectOption | null) => {
        if (!table) return;
        const others = (table.state.filter?.filters || []).filter((f: any) => !(f.field === 'Status' && f.operator === 'eq'));
        if (!value) {
            table.extensions.replaceFilters(others);
            setSelected(null);
        } else {
            table.extensions.replaceFilters([...others, { field: 'Status', operator: 'eq', value: value.value }]);
            setSelected(value);
        }
    };

    return <EnhancedSelect isClearable placeholder='Status...' containerStyle={{ minWidth: '180px' }} options={statusOptions} value={selected} onChange={onChange} isDisabled={!table} />;
}

// Quick filter: narrows the list by partner (sender company name). Applies a
// `SenderName eq <companyName>` filter that coexists with the status filter (AND).
function MessagePartnerFilter(props: { table: any; partners: SelectOption[] }) {
    const { table, partners } = props;
    const [selected, setSelected] = useState<SelectOption | null>(null);

    useEffect(() => {
        const filters = table?.state?.filter?.filters || [];
        const existing = filters.find((f: any) => f.field === 'SenderName' && f.operator === 'eq');
        if (existing) {
            const partner = partners.find(p => p.label === existing.value);
            setSelected(partner || { label: existing.value, value: existing.value });
        } else {
            setSelected(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [table?.state?.filter, partners]);

    const onChange = (value: SelectOption | null) => {
        if (!table) return;
        const others = (table.state.filter?.filters || []).filter((f: any) => !(f.field === 'SenderName' && f.operator === 'eq'));
        if (!value) {
            table.extensions.replaceFilters(others);
            setSelected(null);
        } else {
            table.extensions.replaceFilters([...others, { field: 'SenderName', operator: 'eq', value: value.label }]);
            setSelected(value);
        }
    };

    return <EnhancedSelect isClearable placeholder='Partner...' containerStyle={{ minWidth: '200px' }} options={partners} value={selected} onChange={onChange} isDisabled={!table || !partners.length} />;
}

interface MessageListProps {
    setTitle?: (title: string) => void;
    signalr?: unknown;
    history?: import('history').History;
    location?: import('history').Location;
}

const blankProfile: MessageProfile = { Name: '', Subject: '', Body: '', Shared: true };

function MessageList(props: MessageListProps) {
    const { setTitle, signalr, history, location } = props;
    const coid = parseInt(Auth.getAccountNumber());

    // New Message dialog state
    const [showMessage, setShowMessage] = useState(false);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [messageEmpty, setMessageEmpty] = useState(true);
    const [requireAck, setRequireAck] = useState(false);
    const [recipients, setRecipients] = useState<Array<number | string>>([]);
    const [status, setStatus] = useState('');
    const [externalEmails, setExternalEmails] = useState('');
    const [disableSend, setDisableSend] = useState(false);

    // Error dialog state
    const [showError, setShowError] = useState(false);
    const [error, setError] = useState<string | undefined>();

    // Bulk change-status state
    const [selected, setSelected] = useState<SelectedThread[]>([]);
    const [showChangeStatus, setShowChangeStatus] = useState(false);
    const [updateStatusValue, setUpdateStatusValue] = useState('');
    const [disableUpdate, setDisableUpdate] = useState(false);

    // Templates state
    const [showTemplates, setShowTemplates] = useState(false);
    const [showSaveAs, setShowSaveAs] = useState(false);
    const [showSave, setShowSave] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [profile, setProfile] = useState<MessageProfile>({ ...blankProfile });
    const [profiles, setProfiles] = useState<MessageProfile[]>([]);
    const [bodyEmpty, setBodyEmpty] = useState(true);

    // Filtering / table state
    const [partners, setPartners] = useState<SelectOption[]>([]);
    const [tableUrlBuilder, setTableUrlBuilder] = useState<TableUrlBuilder | undefined>();
    const [hideComplete, setHideComplete] = useState(false);

    const profileSelect = useRef<any>(null);
    // Latest live table handle, captured from the EnhancedTable template callback,
    // so effects (Hide Complete) can act on the current filter state.
    const tableRef = useRef<any>(null);

    const [config, setConfig] = useState<any>(() => ({
        url: Config.api + "/odata/Company/VwCompanyPosts",
        columns: [
            { id: 'Id', hidden: true },
            { id: 'RequireAck', hidden: true, label: "Action Required", filterable: true, type: 'bool' },
            { id: 'Acknowledged', hidden: true, label: "Acknowledged", filterable: true, type: 'bool' },
            { id: 'Sender', hidden: true },
            { id: 'LastReplyByPartner', hidden: true },
            { id: 'DocumentType', hidden: true },
            { id: 'SenderName', filterable: true, label: 'From', width: '16em', getOptions: getCompanyNames },
            { id: 'Subject', filterable: true, allowStringOperators: true, label: 'Subject', template: (row: any) => <div>
                <span style={{verticalAlign: 'middle', fontWeight: row.LastReplyByPartner ? 'bold' : ''}}>{row.Subject}</span>
                {row.Sender !== coid && row.RequireAck && !row.Acknowledged ? <Chip label="Action Required" style={{marginLeft: '0.5rem', backgroundColor: "#EE3224", color: 'white', fontWeight: 'bold', minWidth: '8em' }}/> : ''}
                {!(!row.LastReplyByPartner) && <RepliedIcon style={{color: "#ee3224", fontSize: "10px", marginLeft: "0.5em"}}/>}
                </div> },
            { id: 'DocId', label: 'Document', template: (row: any) => <DocumentLink docType={row.DocumentType} id={row.DocId}>{row.DocId}</DocumentLink>, width: '12em' },
            { id: 'Status', filterable: true, sortable: true, label: 'Status', getOptions: () => Promise.resolve(statusOptions), template: (row: any) => row.Status ? <PostStatusChip status={row.Status}/> : '', width: '15em' },
            { id: 'Date', type:'date', filterable: true, sortable: true, label: 'Date', template: (row: any) => (<Moment format='MM/DD/YYYY hh:mm A'>{row.Date}</Moment>), width: '15em' },
            { id: 'LastUpdated', type:'date', filterable: true, sortable: true, label: 'Last Updated', template: (row: any) => row.LastUpdated ? <Moment format='MM/DD/YYYY hh:mm A'>{row.LastUpdated}</Moment> : '', width: '15em' },
            { id: 'ExternalEmails', label: "CC Email", filterable: true, allowStringOperators: true, width: '8em', template: (row: any) => (<Checkbox checked={!!row.ExternalEmails} style={{pointerEvents: 'none'}}/>) },
            { id: 'Id', width: '10em', stopPropagation: 'true', filterable: false, template: (row: any) => (<Button variant='outlined' component={Link} to={`/message-center/thread?id=${row.Id}`}>View</Button>) }
        ],
        order: 'desc',
        orderBy: 'Id',
        keyField: 'Id',
        pageSize: 20,
        pageSizes: [10, 20, 50, 100],
        filterInUrl: true,
        selectable: true,
        title: 'Messages',
        refresh: false,
        signalr,
        // ThreadId eq null keeps the list to top-level threads only. Marked system
        // so it survives Reset Filters / clear-all and is excluded from saved searches.
        filter: { logic: 'and', filters: [{ field: "ThreadId", operator: 'eq', value: null, system: true }] },
        onGetData: (builder: TableUrlBuilder) => setTableUrlBuilder(builder),
        onSignalrUpdate: signalrUpdate,
        // Functional updater avoids a stale `selected` closure captured at init.
        setSelected: (_ids: Array<number | string>, models: SelectedThread[]) =>
            setSelected(prev => isEqual(prev, models) ? prev : models)
    }));

    // Keep signalr fresh in config (App wires it once the connection is ready).
    useEffect(() => {
        setConfig((prev: any) => prev.signalr === signalr ? prev : { ...prev, signalr });
    }, [signalr]);

    const loadProfiles = useCallback(async (): Promise<MessageProfile[]> => {
        let saved = (await axios.get(Config.api + `/odata/Company/VwImportExportProfiles?$filter=Type eq 'Message'`)).data.value;
        let loaded = saved.map((r: any) => ({...r, Profile: JSON.parse(r.Profile)})).map((r: any) => ({Id: r.Id, Name: r.Name, Body: r.Profile.Body, Subject: r.Profile.Subject}));
        return loaded;
    }, []);

    useEffect(() => {
        if (setTitle) {
            setTitle('Messages');
        }
        (async () => {
            let partnerList = (await axios.get(Config.api + '/api/v1/Partners')).data.Body.Partners.sort((a: any, b: any) => a.CompanyName.localeCompare(b.CompanyName));
            setPartners(partnerList.map((r: any) => ({ label: r.CompanyName, value: r.Id })));
            setProfiles(await loadProfiles());
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Hide Complete toggle: injects/removes a session-only `Status ne Complete`
    // system filter directly on the live table so partner/status quick filters are
    // preserved. 'Complete' is the real backend terminal status. Skipped on mount so
    // URL-restored filters are not clobbered.
    useDidUpdateEffect(() => {
        const table = tableRef.current;
        if (!table) return;
        const current = table.state.filter?.filters || [];
        const without = current.filter((f: any) => !(f.system && f.field === 'Status' && f.operator === 'ne'));
        if (hideComplete) {
            table.extensions.replaceFilters([...without, { field: 'Status', operator: 'ne', value: 'Complete', system: true }]);
        } else {
            table.extensions.replaceFilters(without);
        }
    }, [hideComplete]);

    const exportFn = () => {
        let type = 'xlsx';
        return {
            href: Config.api + `/odata/Company/Functions.ExportPosts?fileType=${type}` + ((tableUrlBuilder && tableUrlBuilder.filter) || '') + ((tableUrlBuilder && tableUrlBuilder.orderBy) || ''),
            fileName: "messages-" + Math.floor(Date.now() / 1000).toString() + '.' + type
        };
    };

    const onError = (err: string) => {
        setError(err);
        setShowError(true);
    };

    const createThread = async () => {
        try {
            setDisableSend(true);
            await axios.post(Config.api + '/odata/Company/Functions.NewThread', {
                Subject: subject,
                Body: message,
                RequireAck: requireAck,
                Recipients: recipients,
                Status: status,
                ExternalEmails: externalEmails
            });
            setShowMessage(false);
            setDisableSend(false);
            setConfig((prev: any) => ({ ...prev, refresh: !prev.refresh }));
        } catch (e) {
            setDisableSend(false);
        }
    };

    const newThread = () => {
        setShowMessage(true);
        setProfile({ ...blankProfile });
        setMessage('');
        setMessageEmpty(true);
        setRequireAck(false);
        setRecipients([]);
        setSubject('');
        setStatus('');
    };

    const submitStatus = async () => {
        setDisableUpdate(true);
        for (let i = 0; i < selected.length; i++) {
            let thread = selected[i];
            try {
                await axios.post(Config.api + '/odata/Company/Functions.UpdatePostStatus', { ThreadId: thread.Id, Status: updateStatusValue });
            } catch {
                // Ignore individual failures; the batch continues.
            }
        }
        setDisableUpdate(false);
        setShowChangeStatus(false);
        setConfig((prev: any) => ({ ...prev, refresh: !prev.refresh }));
    };

    const getProfile = (name: string, profileList?: MessageProfile[]): MessageProfile | undefined => {
        let found = (profileList || profiles).find(r => r.Name === name);
        if (found) {
            found = { ...found };
            found.Body = found.Body || '';
            setProfile(found);
            setBodyEmpty(!found.Body);
        }
        return found;
    };

    const onProfileSelect = (name: string) => {
        if (!name) {
            setProfile({ ...blankProfile });
            setSubject('');
            setMessage('');
            setMessageEmpty(true);
            setBodyEmpty(true);
            return;
        }
        let found = getProfile(name);
        if (found) {
            setSubject(found.Subject || '');
            setMessage(found.Body || '');
            setMessageEmpty(!found.Body);
        }
    };

    const onDelete = async () => {
        let newProfiles = await loadProfiles();
        setProfile({ ...blankProfile });
        setConfirmDelete(false);
        setProfiles(newProfiles);
        if (profileSelect.current) {
            profileSelect.current.setValue('');
        }
    };

    const onSave = async () => {
        let newProfiles = await loadProfiles();
        setShowSave(false);
        setProfiles(newProfiles);
        getProfile(profile.Name, newProfiles);
    };

    const onSaveAs = async (newName: string) => {
        let newProfiles = await loadProfiles();
        setShowSaveAs(false);
        setProfiles(newProfiles);
        getProfile(newName, newProfiles);
        if (profileSelect.current) {
            profileSelect.current.setValue(newName);
        }
    };

    const formatTemplate = (values: MessageProfile) => ({ ...values });

    return (
        <Root style={{ gap: '16px', padding: '24px 14px' }}>
            <Grid container justifyContent='space-between' direction='row' alignItems='center' style={{ width: '100%' }}>
                <Grid item>
                    <PageHeader id='page-title' component='h1'>Messages</PageHeader>
                </Grid>
                <Grid item style={{ display: 'flex', gap: '1em', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button size='small' variant='outlined' onClick={() => setHideComplete(h => !h)}>{hideComplete ? 'Show Complete' : 'Hide Complete'}</Button>
                    <DownloadButton onError={onError} disabled={!tableUrlBuilder} size='small' variant='outlined' onClick={exportFn}>Export</DownloadButton>
                    <Button size='small' onClick={() => { setShowTemplates(true); setProfile({ ...blankProfile }); }} variant='outlined'>Templates</Button>
                    <Button size='small' onClick={newThread} color='primary' variant='contained' style={{ minWidth: '10em' }}>New Message</Button>
                </Grid>
            </Grid>

            <Grid style={{ width: '100%' }}>
                <TypedEnhancedTable config={config} history={history} location={location}
                    template={(table: any) => {
                        tableRef.current = table;
                        return (
                            <TypedCatalogManagerResults
                                table={table}
                                config={config}
                                setConfig={setConfig}
                                history={history}
                                location={location}
                                resultsContainerStyles={defaultResultsContainerStyles}
                                showSavedSearch={true}
                                savedSearchTableType={TABLE_TYPES.MESSAGE_CENTER}
                                filterShowExceptions={['Status', 'SenderName']}
                                isSelectable={true}
                                condensed={false}
                                bulkActions={
                                    <ButtonGroup variant='outlined'>
                                        <Button onClick={() => setShowChangeStatus(true)}>Change Status</Button>
                                    </ButtonGroup>
                                }
                                buttonFilters={
                                    <Grid container style={{ gap: '0.5em', flex: 1 }}>
                                        <MessageStatusFilter table={table} />
                                        <MessagePartnerFilter table={table} partners={partners} />
                                    </Grid>
                                }
                                tableMessage={
                                    table.data.length > 0 ? null : (
                                        <TableCell colSpan={config.columns.length}>
                                            <div style={{ textAlign: 'center', padding: '2em' }}>
                                                <Typography style={{ marginBottom: '1em', color: '#4F4D55' }}>No messages match your filters.</Typography>
                                                <Button variant='outlined' onClick={() => {
                                                    const systemFilters = (table.state.filter?.filters || []).filter((f: any) => f.system && f.field === 'ThreadId');
                                                    table.extensions.replaceFilters(systemFilters);
                                                    setHideComplete(false);
                                                }}>Reset Filters</Button>
                                            </div>
                                        </TableCell>
                                    )
                                }
                            />
                        );
                    }}
                />
            </Grid>

            <Dialog fullWidth={true} maxWidth='md' open={showMessage} onClose={() => setShowMessage(false)}>
                <Toolbar className='lbtoolbar'>New Message</Toolbar>
                <DialogContent style={{flexDirection: 'column'}}>
                    <Typography style={{marginBottom: '1em'}}>To include recipients who are not registered with Logicbroker, you can input their email address(es) in the CC Email field. You may include multiple addresses separated by commas, spaces or semicolons.</Typography>
                    <div style={{flex: 1, display: 'flex', flexDirection: 'row'}}>
                        <TextField style={{flex: 1, marginRight: '1em'}} variant='outlined' placeholder='Subject' disabled={disableSend} value={subject} onChange={(e) => setSubject(e.target.value)} slotProps={{
                            htmlInput: {style: {padding: "8.5px 8.5px 8.5px 16px"}}
                        }}/>
                        <ExtendedSelect variant='outlined' style={{flex: 1, display: 'flex'}} placeholder='Select template...' value={profile.Name} onChange={onProfileSelect} isClearable options={profiles.map(r => ({label: r.Name, value: r.Name}))}/>
                    </div>
                    {partners.length ?
                        <div style={{marginTop: '1em', flexDirection: 'row', display: 'flex'}}>
                        <ExtendedSelect disabled={disableSend} allowSelectAll={true} style={{flex: 1, marginRight: '1em'}} variant='outlined' placeholder='Select recipients...' value={recipients} options={partners} onChange={(e: Array<number | string>) => setRecipients(e)}/>
                        <TextField style={{flex: 1}} variant='outlined' placeholder='CC Email' disabled={disableSend} value={externalEmails} onChange={(e) => setExternalEmails(e.target.value)} slotProps={{
                            htmlInput: {style: {padding: "8.5px 8.5px 8.5px 16px"}}
                        }}/>
                        </div>
                        : ''}
                    <div style={{marginTop: '1em', marginBottom: '1em', flexDirection: 'row', display: 'flex'}}>
                        <ExtendedSelect disabled={disableSend} style={{flex: 1, alignSelf: 'flex-end', marginRight: '1em'}} variant='outlined' placeholder='Status' value={status} options={writeStatusOptions} isClearable onChange={(e: string) => setStatus(e || '')}/>
                        <div style={{flex: 1}}>
                            <FormControlLabel control={<Switch checked={requireAck} disabled={disableSend} onChange={(e, value) => setRequireAck(value)}/>} label='Require Acknowledgement'/>
                        </div>
                    </div>
                    <div style={{height: '200px', display: 'flex'}}>
                        <SimpleRte className='textEditor' disabled={disableSend} value={message} onChange={(html: string, isEmpty: boolean) => { setMessage(html); setMessageEmpty(isEmpty); }}/>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button variant='text' onClick={createThread} disabled={messageEmpty || !subject || !recipients.length || disableSend} color="primary">
                      Send
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={showError} onClose={() => setShowError(false)}>
                <Toolbar className='lbtoolbar'>Error</Toolbar>
                <DialogContent><Typography>{error}</Typography></DialogContent>
            </Dialog>

            <ChangeStatusModal
                open={showChangeStatus}
                onClose={() => setShowChangeStatus(false)}
                value={updateStatusValue}
                onChange={v => setUpdateStatusValue(v)}
                submit={submitStatus}
                disabled={disableUpdate}
            />

            <Dialog open={showTemplates} onClose={() => setShowTemplates(false)} fullWidth maxWidth='md'>
                <Toolbar className='lbtoolbar'>Message Templates</Toolbar>
                <DialogContent style={{display: 'flex', flexDirection: 'column', gap: '1em'}}>
                    <Alert severity="info">
                        <Typography style={{textAlign: 'left'}}>To optimize the use of message templates, we recommend keeping the text generic or creating inputs such as [Supplier Name] to ensure the correct information is present.</Typography>
                    </Alert>
                    <div style={{display: 'flex'}}>
                        <ExtendedSelect innerRef={profileSelect} variant='outlined' style={{flex: '1 1 auto', display: 'flex'}} placeholder='Select template...' value={profile.Name} onChange={onProfileSelect} isClearable options={profiles.map(r => ({label: r.Name, value: r.Name}))}/>
                        <DownloadButton disabled={!profile.Name} style={{marginRight: '1em', marginLeft: '1em'}} variant='outlined' color='primary' onClick={() => setShowSave(true)}>Save</DownloadButton>
                        <Button style={{marginRight: '1em'}} variant='outlined' color='primary' disabled={bodyEmpty} onClick={() => setShowSaveAs(true)}>Save As</Button>
                        <Button disabled={!profile.Name} variant='outlined' color='error' onClick={() => setConfirmDelete(true)}>Delete</Button>
                    </div>
                    <div style={{flex: 1, display: 'flex', flexDirection: 'row'}}>
                        <TextField style={{flex: 1}} variant='outlined' placeholder='Subject' value={profile.Subject || ''} onChange={(e) => setProfile({...profile, Subject: e.target.value})} slotProps={{
                            htmlInput: {style: {padding: "8.5px 8.5px 8.5px 16px"}}
                        }}/>
                    </div>
                    <SimpleRte className='textEditor' value={profile.Body || ''} onChange={(html: string, isEmpty: boolean) => { setProfile({...profile, Body: html}); setBodyEmpty(isEmpty); }}/>
                </DialogContent>
            </Dialog>

            <SaveAsDialog open={showSaveAs} title='Save Template As' shared={true} format={formatTemplate} placeholder='New template name' onClose={() => setShowSaveAs(false)} onSaveAs={onSaveAs} profileType='Message' values={profile}/>
            <SaveDialog open={showSave} type={'template'} shared={true} format={formatTemplate} onClose={() => setShowSave(false)} onSave={onSave} profileType='Message' values={profile}/>
            <DeleteDialog open={confirmDelete} type={'template'} onClose={() => setConfirmDelete(false)} onDelete={onDelete} profileType='Message' values={profile}/>
        </Root>
    );
}

export default MessageList;
